// FieldDesk — экран в iframe-placement'е карточки сделки.
// Контекст сделки получаем через GET /api/bitrix/deals/:dealId/context.
// Показываем только визиты этой сделки + кнопку «Создать выезд» с preset'ом.

import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import type { BitrixDealContext, VisitDto } from '../api/types';
import { ApiError } from '../api/client';
import { TaskCard } from '../components/TaskCard';
import { NewTaskModal } from '../components/NewTaskModal';
import { ErrorStrip } from './Dashboard';

interface Props {
  dealId: string;
  onOpenVisit(v: VisitDto): void;
}

export function EmbeddedDealView({ dealId, onOpenVisit }: Props) {
  const { api } = useAuth();
  const [ctx, setCtx] = useState<BitrixDealContext | null>(null);
  const [visits, setVisits] = useState<VisitDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    setNotFound(false);
    try {
      const dealCtx = await api.getBitrixDealContext(dealId);
      setCtx(dealCtx);
      const vs = await api.listVisits({ bitrixDealId: dealId, limit: 100 });
      setVisits(vs.items);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setNotFound(true);
      } else {
        setErr((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, [api, dealId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (notFound) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🗑️</div>
        <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Сделка не найдена</h2>
        <p style={{ fontSize: 13, color: '#64748b', maxWidth: 420, margin: '0 auto' }}>
          Сделка #{dealId} удалена в Битрикс24 или недоступна вашему пользователю.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 20, height: '100%', overflow: 'auto' }}>
      {loading ? (
        <div style={{ color: '#94a3b8' }}>Загрузка контекста сделки…</div>
      ) : (
        <>
          {ctx && (
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 16,
                marginBottom: 14,
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              }}
            >
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>Сделка Б24 #{ctx.deal.id}</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>{ctx.deal.title}</div>
              {ctx.client ? (
                <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                  <b>{ctx.client.name}</b>
                  <br />
                  <span style={{ color: '#64748b' }}>{ctx.client.address}</span>
                  {ctx.client.phone && (
                    <>
                      <br />
                      <span style={{ color: '#64748b' }}>☎ {ctx.client.phone}</span>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: '#d97706' }}>
                  У сделки не привязана компания. Клиент нужно будет выбрать вручную.
                </div>
              )}
            </div>
          )}

          {err && <ErrorStrip message={err} />}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <h2 style={{ fontSize: 15, fontWeight: 600 }}>Выезды по сделке ({visits.length})</h2>
            <button
              onClick={() => setShowModal(true)}
              style={{
                padding: '8px 14px',
                background: '#2563eb',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              + Создать выезд
            </button>
          </div>

          {visits.length === 0 ? (
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: 24,
                textAlign: 'center',
                color: '#94a3b8',
                fontSize: 13,
              }}
            >
              По этой сделке ещё нет выездов. Нажмите «Создать выезд».
            </div>
          ) : (
            visits.map((v) => <TaskCard key={v.id} visit={v} onClick={onOpenVisit} />)
          )}
        </>
      )}

      {showModal && (
        <NewTaskModal
          onClose={() => setShowModal(false)}
          onCreated={() => {
            setShowModal(false);
            void load();
          }}
          prefill={{
            bitrixDealId: dealId,
            clientId: ctx?.client?.id ?? null,
            clientLabel: ctx?.client?.name ?? null,
          }}
          allowAdHoc={false}
        />
      )}
    </div>
  );
}
